import { useActiveAssetCostCurrency } from '@/asset/hooks/use-active-asset-cost-currency';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { SupportSnackbar } from '@/components/support-snackbar';
import { HISTORICAL_PERIOD_DATE_SHORTCUTS } from '@/constants/date-shortcut';
import { CurrencySelector } from '@/costs/components/currency-selector';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { useDownloadFile } from '@/hooks/use-download-file';
import { getFixedAssetsSnapshot } from '@/report/api/get-fixed-assets-snapshot-report';
import { getInventoryAssetsSnapshot } from '@/report/api/get-inventory-assets-snapshot-report';
import { getScrappedAssetsSnapshot } from '@/report/api/get-scrapped-assets-snapshot-report';
import { getSoldAssetsSnapshot } from '@/report/api/get-sold-assets-snapshot-report';
import { FixedAssetSummaryCards } from '@/report/components/fixed-asset-summary-cards';
import { FixedAssetsSnapshotTable } from '@/report/components/fixed-assets-snapshot-table';
import { InventoryAssetsSnapshotTable } from '@/report/components/inventory-assets-snapshot-table';
import { InventorySummaryCards } from '@/report/components/inventory-summary-cards';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportDateInput } from '@/report/components/report-date-input';
import { ReportExportButton } from '@/report/components/report-export-button';
import { ScrappedAssetSummaryCards } from '@/report/components/scrapped-asset-summary-cards';
import { ScrappedAssetsSnapshotTable } from '@/report/components/scrapped-assets-snapshot-table';
import { SnapshotTruncationNotice } from '@/report/components/snapshot-truncation-notice';
import { SoldAssetSummaryCards } from '@/report/components/sold-asset-summary-cards';
import { SoldAssetsSnapshotTable } from '@/report/components/sold-assets-snapshot-table';
import { useSnapshotCurrencyQuery } from '@/report/hooks/use-snapshot-currency-query';
import { assetSnapshotTypeSchema, assetSnapshotTypes } from '@/report/types';
import { Alert, Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Asset snapshot report';

type AssetSnapshotType = z.infer<typeof assetSnapshotTypeSchema>;

/** Backend export endpoint for each snapshot tab */
const EXPORT_ENDPOINTS: Record<AssetSnapshotType, string> = {
  inventory: '/v1/report/asset/inventory-snapshot/export',
  'fixed-assets': '/v1/report/asset/fixed-snapshot/export',
  'sold-assets': '/v1/report/asset/sold-snapshot/export',
  'scrapped-assets': '/v1/report/asset/scrapped-snapshot/export',
};

const EXPORT_BUTTON_LABELS: Record<AssetSnapshotType, string> = {
  inventory: 'Export inventory snapshot for all currencies',
  'fixed-assets': 'Export fixed asset snapshot for all currencies',
  'sold-assets': 'Export sold asset snapshot for all currencies',
  'scrapped-assets': 'Export scrapped asset snapshot for all currencies',
};

/**
 * Get the description for each snapshot type explaining what assets are included
 */
function getSnapshotDescription(tabType: string): React.ReactNode {
  switch (tabType) {
    case 'inventory':
      return (
        <>
          <strong>Inventory assets</strong> have an inventory date but do not
          yet have an in-service date, sold date, or scrapped date. These assets
          do not appear in any other snapshot for the same date.
        </>
      );
    case 'fixed-assets':
      return (
        <>
          <strong>Fixed assets</strong> have an in-service date, but do not yet
          have a sold date or a scrapped date. They may or may not have an
          inventory date set. These assets do not appear in any other snapshot
          for the same date.
        </>
      );
    case 'sold-assets':
      return (
        <>
          <strong>Sold assets</strong> have been sold, and were sold before they
          were scrapped. They may or may not have an inventory date or an
          in-service date set. These assets do not appear in any other snapshot
          for the same date.
        </>
      );
    case 'scrapped-assets':
      return (
        <>
          <strong>Scrapped assets</strong> have been scrapped, and were scrapped
          before they were sold. They may or may not have an inventory date or
          an in-service date set. These assets do not appear in any other
          snapshot for the same date.
        </>
      );
    default:
      return '';
  }
}

const assetSnapshotSearchSchema = z.object({
  asOfDate: z.string().optional(),
  // The selected cost currency is shared across tabs so it persists on tab clicks. A code
  // outside the app-allowed set reaches the backend and is rejected there with a 400.
  currency: isoCurrencyCodeSchema.optional().catch(undefined),
});

const assetSnapshotParamsSchema = z.object({
  tabType: assetSnapshotTypeSchema,
});

export const Route = createFileRoute('/_app/reports/asset-snapshot/$tabType')({
  component: AssetSnapshotReportRoute,
  validateSearch: assetSnapshotSearchSchema,
  params: assetSnapshotParamsSchema,
});

function AssetSnapshotReportRoute() {
  const search = Route.useSearch();
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const { user } = useCurrentUser();

  const asOfDate = search.asOfDate
    ? DateTime.fromISO(search.asOfDate)
    : DateTime.now();

  // One cost currency shared by every tab (URL-persisted), clamped to one the org actually holds.
  const { availableCurrencies, activeCurrencyCode, isResolved } =
    useActiveAssetCostCurrency(search.currency);

  // Keep the URL on the currency actually rendered, so a clamped deep link is shareable.
  useEffect(() => {
    if (isResolved && search.currency !== activeCurrencyCode) {
      navigate({
        search: (previous) => ({ ...previous, currency: activeCurrencyCode }),
        replace: true,
      });
    }
  }, [activeCurrencyCode, isResolved, navigate, search.currency]);

  // Each snapshot fetches one cost currency (these unpaged responses are large) and
  // refetches when the currency or date changes. Waiting on the resolved currency keeps
  // a provisional one from spending a full fetch the clamp would discard.
  const inventoryAssetsQuery = useSnapshotCurrencyQuery(
    'inventory-assets-snapshot',
    asOfDate,
    isResolved && params.tabType === 'inventory',
    activeCurrencyCode,
    getInventoryAssetsSnapshot
  );

  const fixedAssetsQuery = useSnapshotCurrencyQuery(
    'fixed-assets-snapshot',
    asOfDate,
    isResolved && params.tabType === 'fixed-assets',
    activeCurrencyCode,
    getFixedAssetsSnapshot
  );

  const soldAssetsQuery = useSnapshotCurrencyQuery(
    'sold-assets-snapshot',
    asOfDate,
    isResolved && params.tabType === 'sold-assets',
    activeCurrencyCode,
    getSoldAssetsSnapshot
  );

  const scrappedAssetsQuery = useSnapshotCurrencyQuery(
    'scrapped-assets-snapshot',
    asOfDate,
    isResolved && params.tabType === 'scrapped-assets',
    activeCurrencyCode,
    getScrappedAssetsSnapshot
  );

  // Animation state for each tab's loader
  const [inventoryAnimating, setInventoryAnimating] = useState(true);
  const [fixedAnimating, setFixedAnimating] = useState(true);
  const [soldAnimating, setSoldAnimating] = useState(true);
  const [scrappedAnimating, setScrappedAnimating] = useState(true);

  // Each snapshot response already covers just the active currency.
  const activeInventoryBucket = inventoryAssetsQuery.data;
  const activeFixedBucket = fixedAssetsQuery.data;
  const activeSoldBucket = soldAssetsQuery.data;
  const activeScrappedBucket = scrappedAssetsQuery.data;

  // Reset animation when query starts loading (e.g., date change or initial load)
  useEffect(() => {
    if (inventoryAssetsQuery.isLoading) {
      setInventoryAnimating(true);
    }
  }, [inventoryAssetsQuery.isLoading]);

  useEffect(() => {
    if (fixedAssetsQuery.isLoading) {
      setFixedAnimating(true);
    }
  }, [fixedAssetsQuery.isLoading]);

  useEffect(() => {
    if (soldAssetsQuery.isLoading) {
      setSoldAnimating(true);
    }
  }, [soldAssetsQuery.isLoading]);

  useEffect(() => {
    if (scrappedAssetsQuery.isLoading) {
      setScrappedAnimating(true);
    }
  }, [scrappedAssetsQuery.isLoading]);

  const handleDateChange = (date: DateTime | null) => {
    if (date) {
      navigate({
        search: { ...search, asOfDate: date.toISODate() },
        params: { tabType: params.tabType },
      });
    }
  };

  const handleCurrencyChange = (currencyCode: IsoCurrencyCode) => {
    navigate({
      search: { ...search, currency: currencyCode },
      params: { tabType: params.tabType },
    });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newTabType = assetSnapshotTypes[newValue];

    if (newTabType) {
      navigate({
        search: { ...search },
        params: { tabType: newTabType },
      });
    }
  };

  const downloadExport = useDownloadFile();

  /**
   * Downloads the current tab's backend-generated Excel export (one sheet per
   * currency) via an authenticated request, saved under the server's filename.
   */
  const handleExport = () => {
    const query = new URLSearchParams();
    query.set('asOfDate', asOfDate.toISODate() ?? '');
    downloadExport.mutate({
      path: EXPORT_ENDPOINTS[params.tabType],
      params: query,
      fallbackFilename: `${params.tabType}-snapshot.xlsx`,
    });
  };

  // Get current tab index based on the URL parameter
  const currentTabIndex =
    params.tabType === 'inventory'
      ? 0
      : params.tabType === 'fixed-assets'
        ? 1
        : params.tabType === 'sold-assets'
          ? 2
          : 3;

  return (
    <PageContent
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Error handling for API failures */}
      <SupportSnackbar
        isOpen={
          inventoryAssetsQuery.isError ||
          fixedAssetsQuery.isError ||
          soldAssetsQuery.isError ||
          scrappedAssetsQuery.isError ||
          downloadExport.isError
        }
        onClose={() => {
          downloadExport.reset();
          if (params.tabType === 'inventory') {
            inventoryAssetsQuery.refetch();
          } else if (params.tabType === 'fixed-assets') {
            fixedAssetsQuery.refetch();
          } else if (params.tabType === 'sold-assets') {
            soldAssetsQuery.refetch();
          } else {
            scrappedAssetsQuery.refetch();
          }
        }}
        message="An error occurred while loading the asset snapshot report."
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack>
          <ReportBreadcrumbs title={REPORT_TITLE} parent="accounting" />
          <Typography variant="h5">{REPORT_TITLE}</Typography>
        </Stack>
        <ReportExportButton
          label={
            downloadExport.isPending
              ? 'Exporting...'
              : EXPORT_BUTTON_LABELS[params.tabType]
          }
          isPending={downloadExport.isPending}
          onClick={handleExport}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        justifyContent="flex-end"
        flexWrap="wrap"
      >
        {availableCurrencies.length > 0 && (
          <CurrencySelector
            currencies={availableCurrencies}
            value={activeCurrencyCode}
            onChange={handleCurrencyChange}
          />
        )}
        <ReportDateInput
          date={asOfDate}
          timezone={user.organization.timezone}
          onChange={handleDateChange}
          shortcuts={HISTORICAL_PERIOD_DATE_SHORTCUTS}
          disableFuture={true}
        />
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1 }}>
        <Tabs value={currentTabIndex} onChange={handleTabChange}>
          <Tab label="Inventory assets" />
          <Tab label="Fixed assets" />
          <Tab label="Sold assets" />
          <Tab label="Scrapped assets" />
        </Tabs>
      </Box>

      <Alert severity="info" sx={{ marginTop: 1, marginBottom: 2 }}>
        {getSnapshotDescription(params.tabType)}
      </Alert>

      <Box
        sx={{
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {params.tabType === 'inventory' && (
          <>
            {!inventoryAssetsQuery.isLoading &&
              !inventoryAnimating &&
              activeInventoryBucket && (
                <InventorySummaryCards
                  stats={activeInventoryBucket}
                  currencyCode={activeInventoryBucket.isoCode}
                  isLoading={false}
                />
              )}

            {activeInventoryBucket && (
              <SnapshotTruncationNotice
                shownCount={activeInventoryBucket.assets.length}
                totalCount={activeInventoryBucket.assetCount}
                isExporting={downloadExport.isPending}
                onExport={handleExport}
              />
            )}

            <InventoryAssetsSnapshotTable
              assets={activeInventoryBucket?.assets ?? []}
              isLoading={inventoryAssetsQuery.isLoading}
              isAnimating={inventoryAnimating}
              onAnimationComplete={() => setInventoryAnimating(false)}
              organizationName={user.organization.name}
            />
          </>
        )}
        {params.tabType === 'fixed-assets' && (
          <>
            {!fixedAssetsQuery.isLoading &&
              !fixedAnimating &&
              activeFixedBucket && (
                <FixedAssetSummaryCards
                  stats={activeFixedBucket}
                  currencyCode={activeFixedBucket.isoCode}
                  isLoading={false}
                />
              )}

            {activeFixedBucket && (
              <SnapshotTruncationNotice
                shownCount={activeFixedBucket.assets.length}
                totalCount={activeFixedBucket.assetCount}
                isExporting={downloadExport.isPending}
                onExport={handleExport}
              />
            )}

            <FixedAssetsSnapshotTable
              assets={activeFixedBucket?.assets ?? []}
              isLoading={fixedAssetsQuery.isLoading}
              isAnimating={fixedAnimating}
              onAnimationComplete={() => setFixedAnimating(false)}
              organizationName={user.organization.name}
            />
          </>
        )}
        {params.tabType === 'sold-assets' && (
          <>
            {!soldAssetsQuery.isLoading &&
              !soldAnimating &&
              activeSoldBucket && (
                <SoldAssetSummaryCards
                  stats={activeSoldBucket}
                  currencyCode={activeSoldBucket.isoCode}
                  isLoading={false}
                />
              )}
            {activeSoldBucket && (
              <SnapshotTruncationNotice
                shownCount={activeSoldBucket.assets.length}
                totalCount={activeSoldBucket.assetCount}
                isExporting={downloadExport.isPending}
                onExport={handleExport}
              />
            )}

            <SoldAssetsSnapshotTable
              assets={activeSoldBucket?.assets ?? []}
              isLoading={soldAssetsQuery.isLoading}
              isAnimating={soldAnimating}
              onAnimationComplete={() => setSoldAnimating(false)}
              organizationName={user.organization.name}
            />
          </>
        )}
        {params.tabType === 'scrapped-assets' && (
          <>
            {!scrappedAssetsQuery.isLoading &&
              !scrappedAnimating &&
              activeScrappedBucket && (
                <ScrappedAssetSummaryCards
                  stats={activeScrappedBucket}
                  currencyCode={activeScrappedBucket.isoCode}
                  isLoading={false}
                />
              )}
            {activeScrappedBucket && (
              <SnapshotTruncationNotice
                shownCount={activeScrappedBucket.assets.length}
                totalCount={activeScrappedBucket.assetCount}
                isExporting={downloadExport.isPending}
                onExport={handleExport}
              />
            )}

            <ScrappedAssetsSnapshotTable
              assets={activeScrappedBucket?.assets ?? []}
              isLoading={scrappedAssetsQuery.isLoading}
              isAnimating={scrappedAnimating}
              onAnimationComplete={() => setScrappedAnimating(false)}
              organizationName={user.organization.name}
            />
          </>
        )}
      </Box>
    </PageContent>
  );
}
